import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { atwMarkIdForSystem } from "@/components/Brand/atwProductMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import {
  AdminStepUpDialog,
  postUserAdminAction,
  type AdminStepUpCredentials,
} from "@/components/Users/AdminStepUpDialog";
import { directoryHref } from "@/lib/atwDirectoryHost";
import { ATW_SYSTEMS } from "@/lib/atwSystemAccess";

type AccessRequest = {
  id: string;
  requesterUserId: string;
  requesterEmail: string;
  requesterName?: string | null;
  systemId: string;
  message?: string | null;
  status: "pending" | "approved" | "denied";
  createdAt: string;
};

function systemName(id: string) {
  return ATW_SYSTEMS.find((s) => s.id === id)?.name || id;
}

export function RequestsPage() {
  const state = useDirectoryState();
  const ctx = state.status === "ready" ? state.value : null;
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const filter: "pending" | "all" = searchParams.get("status") === "all" ? "all" : "pending";
  const [pendingAction, setPendingAction] = useState<{
    id: string;
    action: "approve" | "deny";
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/directory/access-request?status=${filter}`), { ...apiFetchInit(), cache: "no-store", });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || "Could not load requests.");
        return;
      }
      setRequests((data.requests || []) as AccessRequest[]);
    } catch {
      toast.error("Could not load requests.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (ctx?.canManageUsers) void load();
  }, [ctx?.canManageUsers, load]);

  if (!ctx) return null;

  if (!ctx.canManageUsers) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Requests</h1>
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          Access requests are here. Your role in this organization is not Organization Owner or
          Account Admin, so the inbox stays locked. Check the account menu for the active org.
        </p>
      </div>
    );
  }

  const confirmResolve = async (creds: AdminStepUpCredentials) => {
    if (!pendingAction) return { error: "No request selected." };
    // Identity step-up uses the same admin-actions gate as editing another person.
    const gate = await postUserAdminAction(ctx.me.id, "edit_gate", creds);
    if ("needsMfa" in gate && gate.needsMfa) return gate;
    if ("error" in gate) return gate;

    const res = await fetch(apiUrl("/api/directory/access-request"), { ...apiFetchInit(), method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pendingAction), });
    const data = await res.json();
    if (!res.ok) return { error: String(data?.error || "Could not update request.") };
    toast.success(
      pendingAction.action === "approve"
        ? data.requesterNotified
          ? "Access granted. Requester notified."
          : "Access granted."
        : data.requesterNotified
          ? "Request denied. Requester notified."
          : "Request denied.",
    );
    setPendingAction(null);
    await load();
    return { ok: true as const };
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {filter === "all" ? "All requests" : "Pending requests"}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Approve or deny system access requests from people in{" "}
          {ctx.org?.companyName || ctx.org?.domain || "your organization"}. Approvals require
          step-up and notify the requester by email.
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#1D232D]">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
          </div>
        ) : requests.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">No requests.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/10">
            {requests.map((req) => (
              <li
                key={req.id}
                className="flex flex-wrap items-start justify-between gap-3 px-4 py-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  {atwMarkIdForSystem(req.systemId) ? (
                    <AtwProductMark
                      id={atwMarkIdForSystem(req.systemId)!}
                      size={32}
                      className="dark:brightness-0 dark:invert"
                      label
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {req.requesterName || req.requesterEmail}
                    </p>
                    <p className="text-sm text-gray-500">
                      {req.requesterEmail} · wants{" "}
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {systemName(req.systemId)}
                      </span>
                    </p>
                    {req.message ? (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{req.message}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-gray-400">
                      {new Date(req.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{req.status}</Badge>
                  <Link to={directoryHref(`/people/${encodeURIComponent(req.requesterUserId)}`)}
                    className="text-sm font-medium text-gray-700 underline-offset-2 hover:underline dark:text-gray-300"
                  >
                    Open person
                  </Link>
                  {req.status === "pending" ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => setPendingAction({ id: req.id, action: "approve" })}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingAction({ id: req.id, action: "deny" })}
                      >
                        Deny
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AdminStepUpDialog
        open={Boolean(pendingAction)}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        title={pendingAction?.action === "approve" ? "Approve access request" : "Deny access request"}
        description="Confirm your identity with your password (and MFA if prompted) before changing another person's access."
        confirmLabel={pendingAction?.action === "approve" ? "Confirm and approve" : "Confirm and deny"}
        onConfirm={confirmResolve}
      />
    </div>
  );
}
